import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';

const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface FileDropZoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  files,
  onFilesChange,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const errors: string[] = [];
    const valid: File[] = [];

    Array.from(incoming).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds the 10 MB limit.`);
      } else {
        valid.push(file);
      }
    });

    const merged = [...files, ...valid].slice(0, MAX_FILES);
    if (files.length + valid.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files allowed.`);
    }

    setFileErrors(errors);
    onFilesChange(merged);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <Grid item xs={12}>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '1px dashed',
          borderColor: isDragOver ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragOver ? 'action.hover' : '#F6F8FC',
          transition: 'background-color 0.15s',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Drag-n-drop or{' '}
          <Box
            component="span"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            browse
          </Box>{' '}
          screenshots or files
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Max 3 files, 10 MB each
        </Typography>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      {fileErrors.map((err, i) => (
        <Typography
          key={i}
          variant="caption"
          color="error"
          display="block"
          mt={0.5}
        >
          {err}
        </Typography>
      ))}
      {files.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1,
          }}
        >
          {files.map((file, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'action.selected',
                borderRadius: 1,
                px: 1,
                py: 0.25,
              }}
            >
              <Typography variant="caption">{file.name}</Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
                sx={{ p: 0 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Grid>
  );
};

export default FileDropZone;
